import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff, X, ArrowLeft, Loader2 } from "lucide-react";

const CONTEXT_LABELS = { post_interview: "Post-Interview", dashboard: "Dashboard" };

function StarDisplay({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="w-3.5 h-3.5"
          fill={n <= rating ? "#F59E0B" : "none"}
          stroke={n <= rating ? "#F59E0B" : "#d1d5db"}
        />
      ))}
    </div>
  );
}

function ScreenshotModal({ src, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-white rounded-xl border border-border rounded-full p-1 hover:bg-red-50 z-10"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        <img src={src} alt="Feedback screenshot" className="max-w-full max-h-[85vh] object-contain rounded-xl border border-border" />
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screenshotSrc, setScreenshotSrc] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/feedback");
        setItems(data);
      } catch {
        toast.error("Could not load feedback");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleRead = async (id, currentRead) => {
    try {
      await api.patch(`/admin/feedback/${id}/read`, { read: !currentRead });
      setItems((prev) => prev.map((f) => f.feedback_id === id ? { ...f, is_read: !currentRead } : f));
    } catch {
      toast.error("Could not update status");
    }
  };

  const deleteFeedback = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((f) => f.feedback_id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    } finally {
      setDeleting(null);
    }
  };

  const unreadCount = items.filter((f) => !f.is_read).length;
  const avgRating = items.length
    ? (items.reduce((s, f) => s + f.rating, 0) / items.length).toFixed(1)
    : "—";

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading feedback…</div>;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {screenshotSrc && <ScreenshotModal src={screenshotSrc} onClose={() => setScreenshotSrc(null)} />}

      <div className="mb-8">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Admin</div>
            <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-1">User Feedback</h1>
          </div>
          <div className="flex items-center gap-6 font-mono text-sm">
            <div>
              <span className="text-muted-foreground text-xs tracking-[0.15em]">TOTAL</span>
              <div className="font-bold text-xl">{items.length}</div>
            </div>
            <div>
              <span className="text-muted-foreground text-xs tracking-[0.15em]">UNREAD</span>
              <div className="font-bold text-xl text-[#0025D8]">{unreadCount}</div>
            </div>
            <div>
              <span className="text-muted-foreground text-xs tracking-[0.15em]">AVG RATING</span>
              <div className="font-bold text-xl text-[#F59E0B]">{avgRating}</div>
            </div>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">No feedback submitted yet.</p>
        </div>
      ) : (
        <div className="border-l border-t border-border">
          <div className="grid grid-cols-12 border-r border-b border-border bg-secondary/40 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <div className="col-span-1 p-3 border-r border-border">Rating</div>
            <div className="col-span-2 p-3 border-r border-border">Candidate</div>
            <div className="col-span-2 p-3 border-r border-border">Context</div>
            <div className="col-span-4 p-3 border-r border-border">Comment</div>
            <div className="col-span-1 p-3 border-r border-border">Screen</div>
            <div className="col-span-1 p-3 border-r border-border">Date</div>
            <div className="col-span-1 p-3" />
          </div>

          {items.map((f) => (
            <div
              key={f.feedback_id}
              className={`grid grid-cols-12 border-r border-b border-border items-start transition-colors ${f.is_read ? "" : "bg-[#0025D8]/[0.03]"}`}
            >
              <div className="col-span-1 p-3 border-r border-border pt-4">
                <StarDisplay rating={f.rating} />
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  {["", "Poor", "Fair", "Good", "Great", "Excellent"][f.rating]}
                </div>
              </div>

              <div className="col-span-2 p-3 border-r border-border">
                <div className="font-semibold text-sm truncate">{f.candidate_name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{f.candidate_email || ""}</div>
                {!f.is_read && (
                  <span className="inline-block mt-1 font-mono text-[9px] uppercase tracking-wider bg-[#0025D8] text-white px-1.5 py-0.5">NEW</span>
                )}
              </div>

              <div className="col-span-2 p-3 border-r border-border">
                <div className="text-xs">{CONTEXT_LABELS[f.context_type] || f.context_type}</div>
                {f.interview_role && (
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5 truncate">{f.interview_role}</div>
                )}
              </div>

              <div className="col-span-4 p-3 border-r border-border">
                {f.comment ? (
                  <p className="text-sm leading-relaxed text-foreground">{f.comment}</p>
                ) : (
                  <span className="text-xs text-muted-foreground italic">No comment</span>
                )}
              </div>

              <div className="col-span-1 p-3 border-r border-border">
                {f.screenshot_b64 ? (
                  <button
                    onClick={() => setScreenshotSrc(f.screenshot_b64)}
                    className="text-[10px] font-mono text-[#0025D8] hover:underline flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" /> View
                  </button>
                ) : (
                  <span className="text-[10px] text-muted-foreground font-mono">—</span>
                )}
              </div>

              <div className="col-span-1 p-3 border-r border-border font-mono text-[10px] text-muted-foreground">
                {new Date(f.created_at).toLocaleDateString()}
              </div>

              <div className="col-span-1 p-3 flex items-center gap-2">
                <button
                  onClick={() => toggleRead(f.feedback_id, f.is_read)}
                  className="text-muted-foreground hover:text-foreground"
                  title={f.is_read ? "Mark unread" : "Mark read"}
                >
                  {f.is_read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => deleteFeedback(f.feedback_id)}
                  disabled={deleting === f.feedback_id}
                  className="text-muted-foreground hover:text-red-500 disabled:opacity-40"
                  title="Delete"
                >
                  {deleting === f.feedback_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
